import React from "react";

interface PasswordResetTemplateProps {
  userName?: string;
  resetUrl: string;
}

const PasswordResetTemplate: React.FC<PasswordResetTemplateProps> = ({
  userName = "Cher utilisateur",
  resetUrl,
}) => {
  const templateHTML = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Réinitialisation du mot de passe - EventToBee 🐝</title>
    <style>
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 0;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: #333;
        }
        .container {
            max-width: 650px;
            margin: 20px auto;
            background: white;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 20px 60px rgba(0,0,0,0.1);
        }
        .header {
            background: linear-gradient(135deg, #4663ac 0%, #3b52a3 100%);
            padding: 20px;
            text-align: center;
            color: white;
            position: relative;
        }
        .header::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="10" cy="10" r="1" fill="white" opacity="0.1"/><circle cx="30" cy="25" r="1.5" fill="white" opacity="0.1"/><circle cx="70" cy="15" r="1" fill="white" opacity="0.1"/><circle cx="90" cy="30" r="1.5" fill="white" opacity="0.1"/></svg>');
        }
        .logo {
            font-size: 64px;
            font-weight: bold;
            margin-bottom: 8px;
            position: relative;
            z-index: 1;
        }
        .reset-title {
            font-size: 24px;
            margin-bottom: 5px;
            position: relative;
            z-index: 1;
        }
        .reset-subtitle {
            font-size: 16px;
            opacity: 0.9;
            position: relative;
            z-index: 1;
        }
        .content {
            padding: 30px;
        }
        .security-banner {
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            border: 2px solid #f59e0b;
            color: #92400e;
            padding: 20px;
            border-radius: 15px;
            margin-bottom: 25px;
            text-align: center;
        }
        .security-icon {
            font-size: 40px;
            margin-bottom: 10px;
        }
        .greeting {
            font-size: 18px;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 20px;
        }
        .message {
            font-size: 16px;
            line-height: 1.6;
            color: #4b5563;
            margin-bottom: 25px;
        }
        .button-container {
            text-align: center;
            margin: 30px 0;
        }
        .reset-button {
            display: inline-block;
            padding: 18px 35px;
            background: linear-gradient(135deg, #4663ac 0%, #3b52a3 100%);
            color: white !important;
            text-decoration: none;
            border-radius: 50px;
            font-weight: 600;
            font-size: 16px;
            box-shadow: 0 10px 25px rgba(70, 99, 172, 0.3);
            transition: all 0.3s ease;
        }
        .reset-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 15px 35px rgba(70, 99, 172, 0.4);
        }
        .security-info {
            background: #f8fafc;
            border-left: 4px solid #4663ac;
            padding: 20px;
            margin: 25px 0;
            border-radius: 0 10px 10px 0;
        }
        .security-info h3 {
            color: #4663ac;
            margin: 0 0 10px 0;
            font-size: 16px;
        }
        .security-info ul {
            margin: 0;
            padding-left: 20px;
            color: #6b7280;
        }
        .security-info li {
            margin-bottom: 8px;
        }
        .expiry-warning {
            background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
            border: 1px solid #60a5fa;
            color: #1d4ed8;
            padding: 15px;
            border-radius: 10px;
            margin: 20px 0;
            text-align: center;
            font-weight: 500;
        }
        .footer {
            background: #f9fafb;
            padding: 30px;
            text-align: center;
            border-top: 1px solid #e5e7eb;
        }
        .footer-text {
            color: #6b7280;
            font-size: 14px;
            margin-bottom: 20px;
        }
        .social-links {
            margin-bottom: 20px;
        }
        .social-links a {
            display: inline-block;
            margin: 0 10px;
            opacity: 0.7;
            transition: opacity 0.3s ease;
        }
        .social-links a:hover {
            opacity: 1;
        }
        .company-info {
            color: #9ca3af;
            font-size: 12px;
            line-height: 1.5;
        }
        @media (max-width: 600px) {
            .container {
                margin: 10px;
            }
            .content {
                padding: 20px;
            }
            .reset-button {
                padding: 15px 25px;
                font-size: 14px;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <!-- Header -->
        <div class="header">
            <div class="logo">🐝 EventToBee</div>
            <div class="reset-title">Réinitialisation du mot de passe</div>
            <div class="reset-subtitle">Sécurité de votre compte</div>
        </div>

        <!-- Content -->
        <div class="content">
            <!-- Security Banner -->
            <div class="security-banner">
                <div class="security-icon">🔐</div>
                <strong>Demande de réinitialisation reçue</strong>
            </div>

            <!-- Greeting -->
            <div class="greeting">Bonjour ${userName},</div>

            <!-- Main Message -->
            <div class="message">
                Nous avons reçu une demande de réinitialisation de mot de passe pour votre compte EventToBee.
                Si vous êtes à l'origine de cette demande, cliquez sur le bouton ci-dessous pour créer un nouveau mot de passe.
            </div>

            <!-- Reset Button -->
            <div class="button-container">
                <a href="${resetUrl}" class="reset-button">
                    🔑 Réinitialiser mon mot de passe
                </a>
            </div>

            <!-- Expiry Warning -->
            <div class="expiry-warning">
                ⏰ <strong>Important :</strong> Ce lien expire dans 1 heure pour votre sécurité.
            </div>

            <!-- Security Information -->
            <div class="security-info">
                <h3>🛡️ Conseils de sécurité</h3>
                <ul>
                    <li><strong>Créez un mot de passe robuste</strong> : Au moins 8 caractères avec majuscules, minuscules, chiffres et symboles</li>
                    <li><strong>N'utilisez jamais le même mot de passe</strong> sur plusieurs sites</li>
                    <li><strong>Activez l'authentification à deux facteurs</strong> quand c'est possible</li>
                    <li><strong>Si ce n'est pas vous</strong> : Ignorez cet email et contactez-nous immédiatement</li>
                </ul>
            </div>

            <div class="message">
                Si vous n'avez pas demandé cette réinitialisation, vous pouvez ignorer cet email en toute sécurité.
                Votre mot de passe actuel restera inchangé.
            </div>
        </div>

        <!-- Footer -->
        <div class="footer">
            <div class="footer-text">
                Cet email a été envoyé automatiquement, merci de ne pas y répondre.
            </div>

            <!-- Social Links -->
            <div class="social-links">
                <a href="https://www.facebook.com/people/Eventtobee/61555527601681/" target="_blank">
                    <img src="https://img.icons8.com/color/32/facebook.png" alt="Facebook" width="32" height="32">
                </a>
                <a href="https://www.instagram.com/eventtobee/" target="_blank">
                    <img src="https://img.icons8.com/color/32/instagram-new.png" alt="Instagram" width="32" height="32">
                </a>
                <a href="https://www.tiktok.com/@eventtobee" target="_blank">
                    <img src="https://img.icons8.com/color/32/tiktok.png" alt="TikTok" width="32" height="32">
                </a>
                <a href="https://www.youtube.com/channel/UCNaEePN03uQbmhZAEqmtDFg" target="_blank">
                    <img src="https://img.icons8.com/color/32/youtube.png" alt="YouTube" width="32" height="32">
                </a>
            </div>

            <div class="company-info">
                © 2024 EventToBee - Plateforme de réservation d'espaces événementiels<br>
                Votre partenaire de confiance pour tous vos événements
            </div>
        </div>
    </div>
</body>
</html>
  `;

  return (
    <div className="email-template-container">
      {/* Preview Header */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">
          🔐 Template de Réinitialisation du Mot de Passe
        </h3>
        <p className="text-sm text-blue-600">
          Template moderne et sécurisé pour la réinitialisation des mots de passe avec design cohérent EventToBee
        </p>
      </div>

      {/* Template Preview */}
      <div className="border border-gray-200 rounded-lg overflow-hidden shadow-lg">
        <div dangerouslySetInnerHTML={{ __html: templateHTML }} />
      </div>

      {/* Copy Button */}
      <div className="mt-6 text-center">
        <button
          onClick={() => {
            navigator.clipboard.writeText(templateHTML);
            alert("Code HTML copié dans le presse-papiers !");
          }}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
        >
          📋 Copier le code HTML
        </button>
      </div>

      {/* Features */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-2">🎨 Design Moderne</h4>
          <p className="text-sm text-gray-600">
            Gradient bleu EventToBee pour la sécurité, design cohérent avec les autres emails EventToBee
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-2">🔒 Sécurité Renforcée</h4>
          <p className="text-sm text-gray-600">
            Conseils de sécurité intégrés, avertissement d'expiration du lien
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-2">📱 Responsive</h4>
          <p className="text-sm text-gray-600">Optimisé pour tous les appareils (mobile, tablette, desktop)</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-2">🔗 Réseaux Sociaux</h4>
          <p className="text-sm text-gray-600">Liens vers tous les réseaux sociaux EventToBee intégrés</p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetTemplate;
